import * as React from "react";
import Login from "./Login/Login";
import Main from "./Main/Main";
import AppContext from "./AppContext";
import EnrichmentInfo, { EnrichmentInfoType } from "../../classes/EnrichmentInfo";
import { IIconProps, MessageBar, MessageBarType } from "office-ui-fabric-react";
import Progress from "./GrayOverlay";
import { _t } from "../../utils/Translator";
import { setRequestAuthConfig } from "../../utils/httpRequest";
import { isMockMode } from "../../mock/mockInterceptor";
const { version } = require("../../../package.json");

// The OnMessageSend event-based runtime (commands.js) runs in a separate
// context from the taskpane and does NOT share localStorage. Office
// roamingSettings IS shared, so we mirror the values the handler needs
// (log targets + auth token/method) into it. Read back in src/commands/onSend.ts.
function roamingSet(key: string, value: string) {
  try {
    const rs = (Office as any)?.context?.roamingSettings;
    if (rs) { rs.set(key, value); rs.saveAsync(); }
  } catch (e) { /* ignore – roamingSettings not available */ }
}
function roamingRemove(key: string) {
  try {
    const rs = (Office as any)?.context?.roamingSettings;
    if (rs) { rs.remove(key); rs.saveAsync(); }
  } catch (e) { /* ignore */ }
}

enum Page {
  Login,
  Main
}

export interface AppProps {
  title: string;
  isOfficeInitialized: boolean;
  itemChangedRegister: any;
}

interface ComposeLogTarget {
  model: string;
  resId: number;
  label: string;
}

function loadEnableLogOnSend(): boolean {
  try {
    const roamingStored = Office?.context?.roamingSettings?.get("enable_log_on_send");
    const stored = typeof roamingStored === "string"
      ? roamingStored
      : localStorage.getItem("enable_log_on_send");
    return stored === "true";
  } catch (e) {
    return false;
  }
}

function loadStoredComposeLogTargets(): ComposeLogTarget[] {
  try {
    const roamingStored = Office?.context?.roamingSettings?.get("compose_log_targets");
    const stored = typeof roamingStored === "string"
      ? roamingStored
      : localStorage.getItem("compose_log_targets");

    if (!stored) {
      return [];
    }

    const parsedTargets = JSON.parse(stored);
    return Array.isArray(parsedTargets) ? parsedTargets : [];
  } catch (e) {
    console.warn("Could not parse compose_log_targets from storage", e);
    return [];
  }
}

export interface AppState {
  mainKey: number;
  pageDisplayed: Page;
  EnrichmentInfo: EnrichmentInfo;
  showPartnerCreatedMessage: boolean;
  showEnrichmentInfoMessage: boolean;
  canCreatePartner: boolean;
  userCompanies: number[];
  loginErrorMessage: string;
  navigation: {
    goToLogin: () => void;
    goToMain: () => void;
  };
  cookies: string;
  connect: (token: string) => void;
  connectWithMicrosoft: (microsoftToken: string, userInfo: any) => void;
  disconnect: () => void;
  getConnectionToken: () => string;
  getAuthMethod: () => string;
  getMicrosoftUserInfo: () => any;
  getUserCompaniesString: () => string;
  isConnected: () => boolean;
  cancelRequests: () => void;
  addRequestCanceller: (canceller: () => void) => void;
  setCanCreatePartner: (canCreatePartner: boolean) => void;
  setUserCompanies: (userCompanies: number[]) => void;
  showTopBarMessage: (enrichmentInfo?: EnrichmentInfo) => void;
  showHttpErrorMessage: (error) => void;
  forceLogoutDueToAuthFailure: (message?: string) => void;
  // Compose mode support
  isComposeMode: boolean;
  composeLogTargets: ComposeLogTarget[];
  addComposeLogTarget: (target: ComposeLogTarget) => void;
  removeComposeLogTarget: (resId: number, model: string) => void;
  clearComposeLogTargets: () => void;
  enableLogOnSend: boolean;
  setEnableLogOnSend: (enabled: boolean) => void;
}

export default class App extends React.Component<AppProps, AppState> {
  requestCancellers: (() => void)[] = [];

  constructor(props, context) {
    super(props, context);

    props.itemChangedRegister(this.onItemChanged);

    // Detect Compose mode: In Compose mode, item.from has a getAsync() method instead of emailAddress property
    const isCompose = typeof Office?.context?.mailbox?.item?.from?.getAsync === 'function';
    
    const storedTargets = loadStoredComposeLogTargets();
    const enableLogOnSend = loadEnableLogOnSend();

    this.state = {
      mainKey: 0,
      EnrichmentInfo: new EnrichmentInfo(),
      showPartnerCreatedMessage: false,
      showEnrichmentInfoMessage: false,
      canCreatePartner: true,
      userCompanies: [],
      pageDisplayed: Page.Main,
      loginErrorMessage: "",
      navigation: {
        goToLogin: this.goToLogin,
        goToMain: this.goToMain
      },
      isComposeMode: isCompose,
      composeLogTargets: storedTargets,
      enableLogOnSend: enableLogOnSend,
      cookies: "",
      connect: token => {
        localStorage.setItem("odooConnectionToken", token);
        roamingSet("odooConnectionToken", token);
        roamingSet("authMethod", "api-key");
      },
      connectWithMicrosoft: (microsoftToken, userInfo) => {
        try {
          // Store Microsoft authentication data
          localStorage.setItem("odooConnectionToken", `Bearer ${microsoftToken}`);
          localStorage.setItem("microsoftToken", microsoftToken);
          localStorage.setItem("microsoftUserInfo", JSON.stringify(userInfo));
          localStorage.setItem("authMethod", "microsoft");
          localStorage.setItem("microsoftLastAuthenticated", new Date().toISOString());
          roamingSet("odooConnectionToken", `Bearer ${microsoftToken}`);
          roamingSet("authMethod", "microsoft");
        } catch (error) {
          console.error("Failed to store Microsoft authentication data:", error);
        }
      },
      disconnect: () => {
        try {
          // Clear all authentication data AND baseURL
          localStorage.removeItem("odooConnectionToken");
          localStorage.removeItem("microsoftToken");
          localStorage.removeItem("microsoftUserInfo");
          localStorage.removeItem("authMethod");
          localStorage.removeItem("microsoftLastAuthenticated");
          localStorage.removeItem("translations");
          localStorage.removeItem("translationsTimestamp");
          localStorage.removeItem("baseURL"); // Clear the Odoo URL so user can re-enter it
          localStorage.removeItem("enable_log_on_send");
          roamingRemove("odooConnectionToken");
          roamingRemove("authMethod");
          roamingRemove("baseURL");
          roamingRemove("compose_log_targets");
          roamingRemove("enable_log_on_send");
        } catch (error) {
          console.error("Failed to clear authentication data:", error);
        }
      },
      getConnectionToken: () => {
        const token = localStorage.getItem("odooConnectionToken");
        // The saved value is already the complete Authorization header.
        return token;
      },
      getAuthMethod: () => {
        return localStorage.getItem("authMethod") || "api-key";
      },
      getMicrosoftUserInfo: () => {
        try {
          const userInfo = localStorage.getItem("microsoftUserInfo");
          return userInfo ? JSON.parse(userInfo) : null;
        } catch (error) {
          console.error("Failed to parse Microsoft user info:", error);
          return null;
        }
      },
      getUserCompaniesString: () => {
        if (this.state.userCompanies.length == 0) {
          return "";
        } else {
          return `&cids=${this.state.userCompanies.sort().join(",")}`;
        }
      },
      isConnected: () => {
        return isMockMode() || !!localStorage.getItem("odooConnectionToken");
      },
      cancelRequests: () => {
        const cancellers = [...this.requestCancellers];
        this.requestCancellers = [];
        for (const canceller of cancellers) {
          canceller(); // Cancel the request.
        }
      },
      addRequestCanceller: (canceller: () => void) => {
        this.requestCancellers.push(canceller);
      },

      setCanCreatePartner: (canCreatePartner: boolean) => {
        this.setState({ canCreatePartner: canCreatePartner });
      },

      setUserCompanies: (companies: number[]) => {
        this.setState({ userCompanies: companies });
      },

      showTopBarMessage: enrichmentInfo => {
        if (enrichmentInfo)
          this.setState({
            EnrichmentInfo: enrichmentInfo,
            showEnrichmentInfoMessage: true
          });
        else
          this.setState({
            EnrichmentInfo: new EnrichmentInfo(EnrichmentInfoType.ConnectionError),
            showEnrichmentInfoMessage: true
          });
      },

      showHttpErrorMessage: (error?) => {
        let errorMessage = "Unknown Error";
        
        if (!error || (typeof error === 'string' && error.trim() === '')) {
          errorMessage = "Request failed. Please try again.";
        } else if (error instanceof Error) {
          // Handle timeout and network errors
          if (error.message?.includes('timeout') || error.message?.includes('Timeout') || error.message?.includes('cancelled')) {
            errorMessage = error.message.includes('cancelled') 
              ? "Request was cancelled."
              : "Request timed out. Please try again.";
          } else if (error.message?.includes('Network') || error.message?.includes('network') || error.message?.includes('Failed to fetch')) {
            errorMessage = "Network error. Please check your connection and try again.";
          } else {
            errorMessage = error.message || "Unknown Error";
          }
        } else if (typeof error === 'string') {
          try {
            const jsonError = JSON.parse(error);
            errorMessage = jsonError?.success === false && jsonError?.error
              ? (typeof jsonError.error === 'string' ? jsonError.error : JSON.stringify(jsonError.error))
              : jsonError?.error?.openapi_message?.error_descrip || jsonError?.error || error;
          } catch (e) {
            errorMessage = error || "Request failed. Please try again.";
          }
        } else if (error && typeof error === 'object') {
          // Structured HTTP error { status, responseText }
          const httpErr = error as { status?: number; responseText?: string; message?: string; error?: any; success?: boolean };
          if (typeof httpErr.responseText === 'string' && httpErr.responseText) {
            try {
              const jsonError = JSON.parse(httpErr.responseText);
              errorMessage = jsonError?.success === false && jsonError?.error
                ? (typeof jsonError.error === 'string' ? jsonError.error : JSON.stringify(jsonError.error))
                : jsonError?.error?.openapi_message?.error_descrip || jsonError?.error || httpErr.responseText;
            } catch (e) {
              errorMessage = httpErr.responseText || "Request failed. Please try again.";
            }
          } else {
            const errorMsg = httpErr.message || httpErr.error || "";
            if (errorMsg.includes('timeout') || errorMsg.includes('Timeout') || errorMsg.includes('cancelled')) {
              errorMessage = errorMsg.includes('cancelled')
                ? "Request was cancelled."
                : "Request timed out. Please try again.";
            } else if (errorMsg.includes('Network') || errorMsg.includes('network') || errorMsg.includes('Failed to fetch')) {
              errorMessage = "Network error. Please check your connection and try again.";
            } else {
              errorMessage = httpErr.success === false && httpErr.error
                ? (typeof httpErr.error === 'string' ? httpErr.error : JSON.stringify(httpErr.error))
                : httpErr.message || httpErr.error || "Request failed. Please try again.";
            }
          }
        }

        this.setState({
          EnrichmentInfo: new EnrichmentInfo(EnrichmentInfoType.OdooCustomError, errorMessage, null),
          showEnrichmentInfoMessage: true
        });
      },

      forceLogoutDueToAuthFailure: (message?: string) => {
        this.state.disconnect();
        this.state.navigation.goToLogin();
        this.setState({ loginErrorMessage: message || "Session expired. Please sign in again." });
      },
      // Compose mode handlers
      addComposeLogTarget: (target: ComposeLogTarget) => {
        this.setState(prevState => {
          const exists = prevState.composeLogTargets.some(
            t => t.resId === target.resId && t.model === target.model
          );
          if (exists) return null; // Don't add duplicates
          const newTargets = [...prevState.composeLogTargets, target];
          localStorage.setItem("compose_log_targets", JSON.stringify(newTargets));
          roamingSet("compose_log_targets", JSON.stringify(newTargets));
          return { composeLogTargets: newTargets };
        });
      },
      removeComposeLogTarget: (resId: number, model: string) => {
        this.setState(prevState => {
          const newTargets = prevState.composeLogTargets.filter(
            t => !(t.resId === resId && t.model === model)
          );
          localStorage.setItem("compose_log_targets", JSON.stringify(newTargets));
          roamingSet("compose_log_targets", JSON.stringify(newTargets));
          return { composeLogTargets: newTargets };
        });
      },
      clearComposeLogTargets: () => {
        localStorage.removeItem("compose_log_targets");
        roamingRemove("compose_log_targets");
        this.setState({ composeLogTargets: [] });
      },
      setEnableLogOnSend: (enabled: boolean) => {
        localStorage.setItem("enable_log_on_send", enabled ? "true" : "false");
        roamingSet("enable_log_on_send", enabled ? "true" : "false");
        this.setState({ enableLogOnSend: enabled });
      }
    };
  }

  componentDidMount() {
    setRequestAuthConfig({
      getAuthMethod: () => localStorage.getItem("authMethod") || "api-key",
      getStoredToken: () => localStorage.getItem("odooConnectionToken"),
      storeMicrosoftToken: (token: string, userInfo?: any) => {
        localStorage.setItem("odooConnectionToken", `Bearer ${token}`);
        localStorage.setItem("microsoftToken", token);
        if (userInfo) localStorage.setItem("microsoftUserInfo", JSON.stringify(userInfo));
        localStorage.setItem("authMethod", "microsoft");
        localStorage.setItem("microsoftLastAuthenticated", new Date().toISOString());
        roamingSet("odooConnectionToken", `Bearer ${token}`);
        roamingSet("authMethod", "microsoft");
      },
      forceLogout: (message?: string) => {
        this.state.forceLogoutDueToAuthFailure(message);
      }
    });

    // Mirror an already-established session into roamingSettings so the
    // OnMessageSend runtime has the token/targets even without a fresh login.
    const existingToken = localStorage.getItem("odooConnectionToken");
    if (existingToken) {
      roamingSet("odooConnectionToken", existingToken);
      roamingSet("authMethod", localStorage.getItem("authMethod") || "api-key");
    }
    const existingBaseURL = localStorage.getItem("baseURL");
    if (existingBaseURL) {
      roamingSet("baseURL", existingBaseURL);
    }
    const existingTargets = localStorage.getItem("compose_log_targets");
    if (existingTargets) {
      roamingSet("compose_log_targets", existingTargets);
    }
    const existingLogOnSend = localStorage.getItem("enable_log_on_send");
    if (existingLogOnSend) {
      roamingSet("enable_log_on_send", existingLogOnSend);
    }

    window.addEventListener("focus", this.syncComposeLogTargetsFromStorage);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  }

  componentDidUpdate(prevProps: AppProps) {
    // Office.initialize fires after the first render, so isComposeMode may have been
    // evaluated before Office was ready (item was null). Re-evaluate once Office is initialized.
    if (!prevProps.isOfficeInitialized && this.props.isOfficeInitialized) {
      const isCompose = typeof Office?.context?.mailbox?.item?.from?.getAsync === 'function';
      if (isCompose !== this.state.isComposeMode) {
        // Force Main to remount so initializePartnerContext runs with correct compose mode
        this.setState({ isComposeMode: isCompose, mainKey: this.state.mainKey + 1 });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("focus", this.syncComposeLogTargetsFromStorage);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    setRequestAuthConfig(null);
  }

  private syncComposeLogTargetsFromStorage = () => {
    this.setState({ composeLogTargets: loadStoredComposeLogTargets() });
  };

  private onVisibilityChange = () => {
    if (!document.hidden) {
      this.syncComposeLogTargetsFromStorage();
    }
  };

  private getMessageBars = () => {
    const { type } = this.state.EnrichmentInfo;
    const message = this.state.EnrichmentInfo.getTypicalMessage();
    const warningIcon: IIconProps = {
      iconName: "Error",
      style: { fontSize: "20px" }
    };
    let bars = [];
    if (this.state.showPartnerCreatedMessage) {
      bars.push(
        <MessageBar messageBarType={MessageBarType.success} onDismiss={this.hidePartnerCreatedMessage}>
          {_t("Contact created")}
        </MessageBar>
      );
    }
    if (this.state.showEnrichmentInfoMessage) {
      switch (type) {
        case EnrichmentInfoType.CompanyCreated:
          bars.push(
            <MessageBar messageBarType={MessageBarType.success} onDismiss={this.hideEnrichmentInfoMessage}>
              {_t("Company created")}
            </MessageBar>
          );
          break;
        case EnrichmentInfoType.CompanyUpdated:
          bars.push(
            <MessageBar messageBarType={MessageBarType.success} onDismiss={this.hideEnrichmentInfoMessage}>
              {_t("Company updated")}
            </MessageBar>
          );
          break;
        case EnrichmentInfoType.NoData:
        case EnrichmentInfoType.NotConnected_NoData:
          bars.push(
            <MessageBar messageBarType={MessageBarType.warning} onDismiss={this.hideEnrichmentInfoMessage}>
              {message}
            </MessageBar>
          );
          break;
        case EnrichmentInfoType.ConnectionError:
          bars.push(
            <>
              <MessageBar
                messageBarType={MessageBarType.error}
                messageBarIconProps={warningIcon}
                onDismiss={this.hideEnrichmentInfoMessage}
              >
                {message}
                <div
                  className="link-like-button"
                  onClick={() => {
                    this.goToLogin();
                  }}
                >
                  {_t("Connect")}
                </div>
              </MessageBar>
            </>
          );
          break;
        case EnrichmentInfoType.EnrichContactWithNoEmail:
        case EnrichmentInfoType.NotConnected_InternalError:
        case EnrichmentInfoType.Other:
        case EnrichmentInfoType.OdooCustomError:
        case EnrichmentInfoType.CouldNotGetTranslations:
          bars.push(
            <MessageBar
              messageBarType={MessageBarType.error}
              messageBarIconProps={warningIcon}
              onDismiss={this.hideEnrichmentInfoMessage}
            >
              {message}
            </MessageBar>
          );
          break;
      }
    }
    return bars;
  };

  private goToLogin = () => {
    this.setState({
      pageDisplayed: Page.Login
    });
  };

  private goToMain = () => {
    this.setState({
      pageDisplayed: Page.Main,
      loginErrorMessage: ""
    });
  };

  private onItemChanged = () => {
    // When we open a new email on Outlook Desktop,
    // we want to reload the component (so we refetch the new partner)
    // Re-detect compose mode (e.g. user transitions from Read to Reply/Forward)
    const isCompose = typeof Office?.context?.mailbox?.item?.from?.getAsync === 'function';
    
    this.setState({
      mainKey: this.state.mainKey + 1,
      isComposeMode: isCompose,
      composeLogTargets: loadStoredComposeLogTargets(),
      showPartnerCreatedMessage: false,
      showEnrichmentInfoMessage: false
    });
  };

  private hideEnrichmentInfoMessage = () => {
    this.setState({
      showEnrichmentInfoMessage: false
    });
  };

  private hidePartnerCreatedMessage = () => {
    this.setState({
      showPartnerCreatedMessage: false
    });
  };

  render() {
    const { isOfficeInitialized } = this.props;

    if (!isOfficeInitialized) {
      return <Progress />;
    }

    switch (this.state.pageDisplayed) {
      case Page.Login:
        return (
          <AppContext.Provider value={this.state}>
            <Login />
          </AppContext.Provider>
        );
      case Page.Main:
      default:
        return (
          <AppContext.Provider value={this.state}>
            <div className="app-main" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div>{this.getMessageBars()}</div>
              <div style={{ flex: 1, overflow: 'auto' }}>
                <Main key={this.state.mainKey} canCreatePartner={this.state.canCreatePartner} />
              </div>
              <div style={{ 
                textAlign: 'center', 
                padding: '4px', 
                fontSize: '9px', 
                color: '#999',
                borderTop: '1px solid #eee'
              }}>
                v{version}
              </div>
            </div>
          </AppContext.Provider>
        );
    }
  }
}
